<script setup lang="ts">
interface OrbitTask { label: string }
interface OrbitAgent { name: string; tag: string; status: string; icon: string; tasks: OrbitTask[] }
interface OrbitContent { label?: string; agents: OrbitAgent[] }
const props = defineProps<{ orbit: OrbitContent }>();
const root = ref<HTMLElement | null>(null);
const orbVideo = ref<HTMLVideoElement | null>(null);
/* Per-task ticked state. Progress is tick-driven: each ticked task raises the
   bar's target by one task-share and the bar glides to it. orbit is static
   (fetched once from the content JSON, never updated post-mount), so these are
   seeded once at setup and no watch on props.orbit is needed. */
const ticked = ref(props.orbit.agents.map((a) => a.tasks.map(() => false)));
const barValues = ref(props.orbit.agents.map(() => 0));
const barTargets = ref(props.orbit.agents.map(() => 0));
let observer: IntersectionObserver | null = null;
let rafId = 0;
let timers: number[] = [];
/* Glide every bar toward its target (~0.45s lag so the bar trails the ticks); stops once settled */
function runBarLoop() {
  if (rafId) return;
  let last = performance.now();
  const step = (now: number) => {
    const dt = Math.min(100, now - last);
    last = now;
    let settled = true;
    barTargets.value.forEach((target, i) => {
      const current = barValues.value[i];
      if (current === undefined) return;
      const diff = target - current;
      if (Math.abs(diff) < 0.4) barValues.value[i] = target;
      else {
        barValues.value[i] = current + diff * (1 - Math.exp(-dt / 450));
        settled = false;
      }
    });
    rafId = settled ? 0 : requestAnimationFrame(step);
  };
  rafId = requestAnimationFrame(step);
}
function tickTask(ai: number, ti: number) {
  const ticks = ticked.value[ai];
  if (!ticks) return;
  ticks[ti] = true;
  barTargets.value[ai] = (ticks.filter(Boolean).length / ticks.length) * 100;
  runBarLoop();
}
function startReveal() {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    ticked.value = props.orbit.agents.map((a) => a.tasks.map(() => true));
    barTargets.value = props.orbit.agents.map(() => 100);
    barValues.value = props.orbit.agents.map(() => 100);
    return;
  }
  /* Every checkbox ticks in, agent by agent */
  props.orbit.agents.forEach((agent, ai) => {
    agent.tasks.forEach((_task, ti) => {
      timers.push(window.setTimeout(() => tickTask(ai, ti), 400 + ai * 800 + ti * 500));
    });
  });
}
onMounted(() => {
  /* The orb shows on desktop only; skip playback below 1024px so the video
     doesn't decode in the background, and for reduced-motion users */
  if (
    orbVideo.value &&
    window.matchMedia("(min-width: 1024px)").matches &&
    !window.matchMedia("(prefers-reduced-motion: reduce)").matches
  ) {
    orbVideo.value.play().catch(() => {});
  }
  if (!root.value) return;
  observer = new IntersectionObserver(
    (entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        observer?.disconnect();
        observer = null;
        startReveal();
      }
    },
    { threshold: 0 },
  );
  observer.observe(root.value);
});
onBeforeUnmount(() => {
  observer?.disconnect();
  cancelAnimationFrame(rafId);
  timers.forEach((t) => clearTimeout(t));
});
</script>
<template>
  <div ref="root" class="relative w-full max-w-[1104px] mx-auto lg:aspect-[1104/550]">
    <div class="va-orbit-dots" aria-hidden="true"></div>
    <svg class="va-orbit-wires" viewBox="0 0 1104 550" fill="none" aria-hidden="true">
      <!-- Elbow wires: card edge -> rounded corner -> down/up -> rounded corner -> orb edge.
           Card edges: left x=369, right x=701; card mid-heights y=100 / y=390; orb center (535, 245). -->
      <path class="va-orbit-wire" d="M369 100 H405 Q417 100 417 112 V233 Q417 245 429 245 H449" />
      <path class="va-orbit-wire" d="M701 100 H665 Q653 100 653 112 V233 Q653 245 641 245 H621" />
      <path class="va-orbit-wire" d="M369 390 H405 Q417 390 417 378 V257 Q417 245 429 245 H449" />
      <path class="va-orbit-wire" d="M701 390 H665 Q653 390 653 378 V257 Q653 245 641 245 H621" />
      <!-- Shimmer: a short bright pulse traveling card -> orb, all in sync -->
      <path class="va-orbit-wire-shimmer" pathLength="100" d="M369 100 H405 Q417 100 417 112 V233 Q417 245 429 245 H449" />
      <path class="va-orbit-wire-shimmer" pathLength="100" d="M701 100 H665 Q653 100 653 112 V233 Q653 245 641 245 H621" />
      <path class="va-orbit-wire-shimmer" pathLength="100" d="M369 390 H405 Q417 390 417 378 V257 Q417 245 429 245 H449" />
      <path class="va-orbit-wire-shimmer" pathLength="100" d="M701 390 H665 Q653 390 653 378 V257 Q653 245 641 245 H621" />
    </svg>
    <video
      ref="orbVideo"
      class="va-orbit-orb"
      src="/videos/vaah-agent-orb.webm"
      :muted="true"
      loop
      playsinline
      preload="none"
      aria-hidden="true"
    />
    <span class="va-orbit-orb-label">{{ orbit.label || "VaahAgents" }}</span>
    <div class="va-orbit-cards">
      <article
        v-for="(agent, i) in orbit.agents"
        :key="agent.name"
        class="va-orbit-card"
        :class="'va-orbit-pos-' + (i + 1)"
      >
        <div class="va-orbit-card-head">
          <span class="va-orbit-avatar">
            <WrSVGIcon name="WrRobotIcon" class="size-[16px] text-white" />
          </span>
          <div class="va-orbit-card-titles">
            <p class="va-orbit-card-name">{{ agent.name }}</p>
            <p class="va-orbit-card-status">{{ agent.status }}</p>
          </div>
          <span class="va-orbit-card-tag">{{ agent.tag }}</span>
        </div>
        <div class="va-orbit-card-body">
          <ul class="va-orbit-tasks">
            <li
              v-for="(task, ti) in agent.tasks"
              :key="ti"
              class="va-orbit-task"
              :class="{ 'is-ticked': ticked[i]?.[ti] }"
            >
              <svg class="va-orbit-task-dot" viewBox="0 0 16 16" aria-hidden="true">
                <circle cx="8" cy="8" r="6.4" fill="none" stroke="rgba(82,115,254,0.55)" stroke-width="1.4" />
                <circle class="va-orbit-task-dot-fill" cx="8" cy="8" r="7" fill="#5273FE" />
                <path class="va-orbit-task-dot-check" d="M4.9 8.3l2 2 4.2-4.6" stroke="#fff" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              <span>{{ task.label }}</span>
            </li>
          </ul>
          <div class="va-orbit-card-icon">
            <WrIcon :name="agent.icon" :size="22" color="currentColor" />
          </div>
        </div>
        <div class="va-orbit-progress">
          <div class="va-orbit-progress-track">
            <div class="va-orbit-progress-fill" :style="{ width: barValues[i] + '%' }" />
          </div>
          <span class="va-orbit-progress-label">{{ Math.round(barValues[i] ?? 0) }}%</span>
        </div>
      </article>
    </div>
  </div>
</template>
<style scoped>
/* ── Stage ─────────────────────────────────────────────── */
.va-orbit-dots,
.va-orbit-wires,
.va-orbit-orb,
.va-orbit-orb-label {
  display: none;
}
/* The central orb (video + label) is hidden on mobile and tablet; it only
   re-appears from the desktop breakpoint (1024px) up. */
/* The label stays a text overlay (over the video) so it can come from the content JSON */
.va-orbit-orb-label {
  position: absolute;
  left: 50%;
  z-index: 3;
  transform: translate(-50%, -50%);
  font-family: "ITC Avant Garde Gothic", "AvantGarde", sans-serif;
  font-weight: 700;
  font-size: clamp(12px, 1.6vw, 18px);
  letter-spacing: 0.2px;
  color: #fff;
  text-shadow: 0 1px 10px rgba(20, 15, 80, 0.9);
}
/* (The central orb is desktop-only: the old tablet media query that centered
   it above the card grid was removed, so it stays display:none below 1024px.) */
/* ── Cards (mobile/tablet: stacked grid) ──────────────── */
.va-orbit-cards {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  /* No top gap: the central orb is gone on mobile/tablet, so the grid sits
     directly under the hero copy (the hero wrapper already adds the gap) */
  margin-top: 0;
}
@media (min-width: 640px) {
  .va-orbit-cards {
    grid-template-columns: 1fr 1fr;
  }
}
/* ── Card ─────────────────────────────────────────────── */
.va-orbit-card {
  display: flex;
  flex-direction: column;
  /* border-image can't follow border-radius, so the border gradient lives on
     the border-box layer and the fill on the padding-box layer */
  background:
    linear-gradient(196deg, rgba(4, 7, 23, 0) 61.83%, #03031f 88.29%) padding-box,
    linear-gradient(290deg, rgba(56, 112, 181, 0.68) 0.56%, #0b0b26 97.55%) border-box;
  border: 1px solid transparent;
  border-radius: 12px;
  padding: 13px 12px 14px;
  box-shadow: 0 10px 30px rgba(2, 4, 20, 0.45);
}
.va-orbit-card-head {
  position: relative;
  display: grid;
  grid-template-columns: auto 1fr;
  column-gap: 10px;
  align-items: center;
}
.va-orbit-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #3b5bdb 0%, #23349c 100%);
  box-shadow: 0 0 10px rgba(70, 100, 240, 0.45);
}
.va-orbit-card-titles {
  min-width: 0;
}
.va-orbit-card-name {
  font-family: "ITC Avant Garde Gothic", "AvantGarde", sans-serif;
  font-size: 15px;
  font-weight: 600;
  line-height: 18px;
  color: #6ca3ff;
}
.va-orbit-card-status {
  font-size: 11.5px;
  line-height: 15px;
  color: #4d78d3;
  margin-top: 1px;
}
.va-orbit-card-tag {
  position: absolute;
  top: 0;
  right: 0;
  font-size: 12px;
  font-weight: 600;
  color: #7b6af1;
  white-space: nowrap;
}
.va-orbit-card-body {
  display: flex;
  gap: 10px;
  flex: 1;
  margin-top: 16px;
}
.va-orbit-tasks {
  flex: 1;
  min-width: 0;
  list-style: none;
  margin: 0;
  padding: 0;
}
.va-orbit-task {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 12px;
  line-height: 14px;
  color: #8a93ad;
  margin-bottom: 3px;
  transition: color 0.3s ease;
}
.va-orbit-task:last-child {
  margin-bottom: 0;
}
.va-orbit-task.is-ticked {
  color: #c3cadb;
}
.va-orbit-task-dot {
  flex: none;
  width: 14px;
  height: 14px;
}
/* Tick-in: the blue disc pops in, then the checkmark draws itself */
.va-orbit-task-dot-fill {
  opacity: 0;
  transform: scale(0.3);
  transform-box: fill-box;
  transform-origin: center;
  transition:
    opacity 0.2s ease,
    transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.va-orbit-task-dot-check {
  stroke-dasharray: 10;
  stroke-dashoffset: 10;
  transition: stroke-dashoffset 0.3s ease 0.12s;
}
.va-orbit-task.is-ticked .va-orbit-task-dot-fill {
  opacity: 1;
  transform: scale(1);
}
.va-orbit-task.is-ticked .va-orbit-task-dot-check {
  stroke-dashoffset: 0;
}
.va-orbit-card-icon {
  flex: none;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  /* the icon uses currentColor, so its hue is themed from this wrapper */
  color: #9AA0FF;
  background: linear-gradient(135deg, #16226e 0%, #0e1850 100%);
  border: 1px solid rgba(90, 110, 240, 0.35);
  box-shadow: 0 0 14px rgba(50, 70, 200, 0.35);
}
.va-orbit-progress {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
}
.va-orbit-progress-track {
  flex: 1;
  height: 4px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.07);
  overflow: hidden;
}
.va-orbit-progress-fill {
  height: 100%;
  min-width: 10px;
  border-radius: 999px;
  background: linear-gradient(90deg, #5b8cff, #7aa0ff);
  box-shadow: 0 0 8px rgba(91, 140, 255, 0.8);
}
.va-orbit-progress-label {
  font-size: 11px;
  color: #7b88cc;
}
@media (min-width: 1024px) {
  .va-orbit-dots {
    display: block;
    position: absolute;
    inset: 0;
    background-image: radial-gradient(rgba(110, 130, 230, 0.3) 1px, rgba(0, 0, 0, 0) 1.6px);
    background-size: 16px 16px;
    -webkit-mask-image: radial-gradient(ellipse 44% 56% at 48.5% 44.5%, #000 0%, rgba(0, 0, 0, 0.5) 55%, transparent 75%);
    mask-image: radial-gradient(ellipse 44% 56% at 48.5% 44.5%, #000 0%, rgba(0, 0, 0, 0.5) 55%, transparent 75%);
    pointer-events: none;
  }
  .va-orbit-wires {
    display: block;
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
  }
  .va-orbit-wire {
    stroke: rgba(99, 102, 241, 0.4);
    stroke-width: 1.5;
  }
  /* Shimmer: a soft glow of light traveling card -> orb; the blur turns the
     short dash into a blurred orb instead of a line */
  .va-orbit-wire-shimmer {
    stroke: rgba(196, 205, 255, 0.9);
    stroke-width: 1;
    stroke-linecap: round;
    stroke-dasharray: 20 80;
    stroke-dashoffset: 100;
    animation: va-wire-shimmer 3s linear infinite;
    filter: blur(2px) drop-shadow(0 0 5px rgba(150, 160, 255, 0.9));
  }
  .va-orbit-orb {
    display: block;
    position: absolute;
    left: 48.46%;
    top: 44.55%;
    width: 34%;
    transform: translate(-50%, -50%);
    z-index: 2;
  }
  .va-orbit-orb-label {
    display: block;
    left: 48.46%;
    top: 44.55%;
  }
  .va-orbit-cards {
    display: block;
    position: absolute;
    inset: 0;
    margin: 0;
  }
  .va-orbit-card {
    position: absolute;
    width: 25.18%;
    z-index: 3;
    /* Grow the whole card (box + inner content) proportionally. Each card's
       transform-origin (below) pins its inner edge so it scales outward while
       the wires stay connected */
    transform: scale(1.25);
  }
  .va-orbit-pos-1,
  .va-orbit-pos-3 {
    left: 8.24%;
  }
  .va-orbit-pos-2,
  .va-orbit-pos-4 {
    left: 63.49%;
  }
  .va-orbit-pos-1,
  .va-orbit-pos-2 {
    top: 2.73%;
  }
  .va-orbit-pos-3,
  .va-orbit-pos-4 {
    top: 55.45%;
  }
  /* Pin each card's inner edge (where the wire meets it) so scaling grows the
     card outward instead of drifting toward the orb */
  .va-orbit-pos-1 {
    transform-origin: top right;
  }
  .va-orbit-pos-2 {
    transform-origin: top left;
  }
  .va-orbit-pos-3 {
    transform-origin: bottom right;
  }
  .va-orbit-pos-4 {
    transform-origin: bottom left;
  }
}
/* Shimmer pulse travels the full path length (normalized via pathLength=100) */
@keyframes va-wire-shimmer {
  to {
    stroke-dashoffset: 0;
  }
}
/* ── Light theme ───────────────────────────────────────── */
/* Card fill stays opaque (a transparent top would let the page's blue wash
   bleed through), with a subtle cool tint; glows are softened so they don't
   read as dark halos on white */
html:not(.my-app-dark) .va-orbit-card {
  background:
    linear-gradient(196deg, #faf5ff 0%, #ffffff 55%) padding-box,
    linear-gradient(290deg, rgba(148, 103, 192, 0.55) 0.56%, rgba(148, 103, 192, 0.35) 97.55%) border-box;
  box-shadow: 0 8px 24px rgba(80, 29, 113, 0.1);
}
html:not(.my-app-dark) .va-orbit-avatar {
  /* lightened so the avatar doesn't read as a dark blob on the white card;
     still dark enough for the white robot glyph to stay legible */
  background: linear-gradient(135deg, #8b5cf6 0%, #6d2d9e 100%);
  box-shadow: 0 0 8px rgba(148, 103, 192, 0.35);
  /* the glyph is a CSS icon colored via currentColor, so pin it white here —
     the color prop on WrIcon doesn't reach it, and it would otherwise inherit
     the page's dark text color in the light theme */
  color: #fff;
}
html:not(.my-app-dark) .va-orbit-card-name {
  color: #501d71;
}
html:not(.my-app-dark) .va-orbit-card-status {
  color: #6d2d9e;
}
html:not(.my-app-dark) .va-orbit-card-tag {
  color: #6d2d9e;
}
html:not(.my-app-dark) .va-orbit-task {
  color: #5a5478;
}
html:not(.my-app-dark) .va-orbit-task.is-ticked {
  color: #501d71;
}
html:not(.my-app-dark) .va-orbit-card-icon {
  color: #6d2d9e;
  background: linear-gradient(135deg, #f3ecff 0%, #e9d8ff 100%);
  border-color: rgba(148, 103, 192, 0.25);
  box-shadow: 0 0 12px rgba(148, 103, 192, 0.18);
}
html:not(.my-app-dark) .va-orbit-progress-track {
  background: rgba(80, 29, 113, 0.12);
}
html:not(.my-app-dark) .va-orbit-progress-fill {
  box-shadow: 0 0 6px rgba(148, 103, 192, 0.5);
}
html:not(.my-app-dark) .va-orbit-progress-label {
  color: #6d2d9e;
}
/* The near-white shimmer glow would wash out on a light background */
html:not(.my-app-dark) .va-orbit-wire-shimmer {
  stroke: rgba(148, 103, 192, 0.9);
  filter: blur(2px) drop-shadow(0 0 5px rgba(148, 103, 192, 0.55));
}
@media (prefers-reduced-motion: reduce) {
  .va-orbit-wire-shimmer {
    animation: none;
    opacity: 0;
  }
  .va-orbit-task,
  .va-orbit-task-dot-fill,
  .va-orbit-task-dot-check {
    transition: none;
  }
}
</style>
